import { AlertCircle, Check, Cloud, Loader2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { LinkButton } from "./ui/buttons";

export type AutosaveState =
  | { kind: "local" }
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved"; at: Date }
  | { kind: "error"; retry: () => void };

/** "Sparas automatiskt" and its four states; the error state carries a retry. */
export function AutosaveIndicator({ state }: { state: AutosaveState }) {
  const { t, i18n } = useTranslation("onboarding");
  const base = "inline-flex items-center gap-1.5 text-[12.5px] font-medium";
  switch (state.kind) {
    case "local":
      return (
        <span className={cn(base, "text-g-ink-500")}>
          <Cloud className="h-3.5 w-3.5" aria-hidden="true" />
          {t("meta.localSave")}
        </span>
      );
    case "idle":
      return (
        <span className={cn(base, "text-g-ink-500")}>
          <Cloud className="h-3.5 w-3.5" aria-hidden="true" />
          {t("meta.autosave")}
        </span>
      );
    case "saving":
      return (
        <span className={cn(base, "text-g-ink-500")} aria-live="polite">
          <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
          {t("meta.saving")}
        </span>
      );
    case "saved":
      return (
        <span className={cn(base, "text-g-success")} aria-live="polite">
          <Check className="h-3.5 w-3.5" aria-hidden="true" />
          {t("meta.savedAt", {
            time: state.at.toLocaleTimeString(i18n.language, {
              hour: "2-digit",
              minute: "2-digit",
            }),
          })}
        </span>
      );
    case "error":
      return (
        <span className={cn(base, "text-g-danger")} role="alert">
          <AlertCircle className="h-3.5 w-3.5" aria-hidden="true" />
          {t("meta.saveError")}
          <LinkButton onClick={state.retry} className="text-[12.5px]">
            {t("meta.retry")}
          </LinkButton>
        </span>
      );
  }
}
